import type React from "react";
import { useState } from "react";

import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useDeleteQuickReply, useGetQuickReplies, usePostQuickReply } from "api/services/messaging/hooks";
import type { PostQuickReplyArgs } from "api/services/messaging/schemas/quick_replies";
import { PostQuickReplyArgsSchema, type QuickReply } from "api/services/messaging/schemas/quick_replies";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { CloseIcon } from "shared/icons";
import { DARK_BLUE } from "theme/colors";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { reportError } from "utils/errorReporting";

interface Props {
  onQuickReplySelected: (quickReply: string) => void;
  width?: string;
}

export const QuickRepliesForm: React.FC<Props> = ({ onQuickReplySelected, width = "480px" }) => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const [editMode, setEditMode] = useState(false);

  invariant(profile);

  const { data: quickRepliesData = [], refetch } = useGetQuickReplies(profile.id);
  const deleteQuickReplyMutation = useDeleteQuickReply();
  const postQuickReplyMutation = usePostQuickReply();

  const form = useForm<PostQuickReplyArgs>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      text: "",
      userID: profile.id,
    },
    resolver: zodResolver(PostQuickReplyArgsSchema),
  });

  const handleClick = (quickReply: QuickReply) => {
    AnalyticsService.track(AnalyticsEvents.QUICK_REPLIES.USED_FORM_QUICK_REPLY);
    onQuickReplySelected(quickReply.text);
  };

  const handleDelete = async (quickReply: QuickReply) => {
    try {
      await deleteQuickReplyMutation.mutateAsync(
        { userID: profile.id, id: quickReply.id },
        {
          onSuccess() {
            AnalyticsService.track(AnalyticsEvents.QUICK_REPLIES.DELETED_QUICK_REPLY);
            refetch();
          },
        }
      );
    } catch (error) {
      reportError("QuickRepliesForm handleDelete error", new Error(JSON.stringify(error)));
    }
  };

  const onSubmit = form.handleSubmit(async formData => {
    try {
      await postQuickReplyMutation.mutateAsync(formData, {
        onSuccess() {
          AnalyticsService.track(AnalyticsEvents.QUICK_REPLIES.SAVED_QUICK_REPLY);
          refetch();
        },
      });
      form.reset();
    } catch (error) {
      reportError("QuickRepliesForm onSubmit error", new Error(JSON.stringify(error)));
    }
  });

  return (
    <Container data-testid="quick-replies-form" $width={width}>
      <Stack direction="row" height={40} mb={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="body_bold">{t("messages.quick_replies")}</Typography>
        {editMode ? (
          <IconButton onClick={() => setEditMode(false)}>
            <CloseIcon />
          </IconButton>
        ) : (
          <BaseButton
            variant="action"
            text={t("buttons.edit")}
            onClick={() => setEditMode(true)}
            icon="pen-to-square"
            dataTestID="quick-replies-form-edit-mode-button"
          />
        )}
      </Stack>

      <ChipContainer $editMode={editMode}>
        {quickRepliesData?.map((quickReply, index) => {
          return (
            <Chip
              key={quickReply.id}
              data-testid={`form-quick-reply-${index}`}
              label={quickReply.text}
              onClick={
                editMode
                  ? undefined
                  : () => {
                      handleClick(quickReply);
                    }
              }
              onDelete={
                editMode
                  ? () => {
                      handleDelete(quickReply);
                    }
                  : undefined
              }
              deleteIcon={
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
                <div
                  role="button"
                  onClick={event => {
                    event.stopPropagation();
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} fontSize="16px" color={DARK_BLUE[100]} />
                </div>
              }
            />
          );
        })}
      </ChipContainer>

      {editMode && (
        <FormProvider {...form}>
          <Stack direction="row" spacing={1}>
            <Controller
              name="text"
              control={form.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoFocus
                  placeholder={t("buttons.add")}
                  fullWidth
                  size="small"
                  error={!!form.formState.errors.text}
                  helperText={!!form.formState.errors.text && t("errors.field.required")}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      onSubmit();
                    }
                  }}
                  slotProps={{
                    htmlInput: {
                      "data-testid": "new-quick-reply-input",
                    },
                  }}
                />
              )}
            />
            <BaseButton
              text={t("buttons.add")}
              onClick={onSubmit}
              size="large"
              square
              style={{ flexBasis: "auto", flexGrow: 0, padding: "12px 16px" }}
              dataTestID="new-quick-reply-save-button"
            />
          </Stack>
        </FormProvider>
      )}
    </Container>
  );
};

const Container = styled.div<{ $width: string }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 300px;
  max-height: 400px;
  width: ${({ $width }) => $width};

  ${props => props.theme.belowMobileBreakpoint} {
    width: auto;
    height: 100%;
    max-height: none;
    padding: 16px;
  }
`;

const ChipContainer = styled.div<{ $editMode: boolean }>`
  flex: 1;
  overflow: auto;

  ${props => props.theme.aboveBreakpoint} {
    /* width */
    ::-webkit-scrollbar {
      width: 4px;
    }

    /* Track */
    ::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .MuiChip-root {
    background-color: ${({ theme, $editMode }) => ($editMode ? theme.colors.redesign.db10 : theme.colors.redesign.b10)};
    color: ${({ theme, $editMode }) => ($editMode ? theme.colors.redesign.db60 : theme.colors.redesign.b100)};
    font-size: 16px;
    font-weight: 500;
    height: auto;
    min-height: 35px;
    margin: 0 12px 12px 0;
    border-radius: 50px;

    &:hover {
      background-color: ${({ theme, $editMode }) => !$editMode && theme.colors.redesign.b30};
    }
  }

  .MuiChip-label {
    white-space: pre-wrap;
    padding: 6px 12px;
  }

  .MuiChip-deleteIcon {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 6px 0 -6px;
  }
`;
